import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  Users,
  Ear,
  Stethoscope,
  Building2,
  Calendar,
  BarChart3,
  FileText,
  Settings,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Package,
  UserCog,
  MessageCircle,
  Bell,
  Video,
  Mic,
  Target,
  ShoppingCart,
  Activity,
  Cloud,
  Layers,
  Headphones
} from "lucide-react";

const DashboardLayout = ({ children }) => {
  const { user, logout, isSuperAdmin, isClinicAdmin, isAudiologist } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navItems = [
    {
      label: "Dashboard",
      icon: LayoutDashboard,
      href: "/dashboard",
      roles: ["super_admin", "clinic_admin", "audiologist", "sales_crm", "head", "business_team", "clinic_manager", "front_desk"]
    },
    {
      label: "Patients",
      icon: Users,
      href: "/patients",
      roles: ["super_admin", "clinic_admin", "audiologist", "sales_crm", "head", "business_team", "clinic_manager", "front_desk"]
    },
    {
      label: "Sales Pipeline",
      icon: Target,
      href: "/leads",
      roles: ["super_admin", "clinic_admin", "sales_crm", "head", "business_team", "clinic_manager"]
    },
    {
      label: "Trials & Sales",
      icon: ShoppingCart,
      href: "/trials",
      roles: ["super_admin", "clinic_admin", "audiologist", "sales_crm", "head", "business_team", "clinic_manager"]
    },
    {
      type: "divider",
      roles: ["super_admin", "audiologist", "head"]
    },
    {
      label: "PTA Test",
      icon: Ear,
      href: "/pta-test",
      roles: ["super_admin", "audiologist", "head"]
    },
    {
      label: "Live PTA Test",
      icon: Headphones,
      href: "/pta-live",
      roles: ["super_admin", "audiologist", "clinic_admin"],
      badge: "NEW"
    },
    {
      label: "Live Monitor",
      icon: Activity,
      href: "/live-monitor",
      roles: ["super_admin", "audiologist", "clinic_admin"],
      badge: "LIVE"
    },
    {
      label: "Test Sessions",
      icon: Users,
      href: "/test-sessions",
      roles: ["super_admin", "audiologist", "clinic_admin", "sales_crm", "front_desk"]
    },
    {
      label: "Speech Test",
      icon: Mic,
      href: "/speech-test",
      roles: ["super_admin", "audiologist", "head"]
    },
    {
      label: "Tele-Audiology",
      icon: Video,
      href: "/tele-audiology",
      roles: ["super_admin", "clinic_admin", "audiologist", "head"]
    },
    {
      label: "Recommendations",
      icon: Stethoscope,
      href: "/recommendations",
      roles: ["super_admin", "clinic_admin", "audiologist", "head"]
    },
    {
      label: "Appointments",
      icon: Calendar,
      href: "/appointments",
      roles: ["super_admin", "clinic_admin", "audiologist", "sales_crm", "head", "business_team", "clinic_manager", "front_desk"]
    },
    {
      label: "Product Catalog",
      icon: Package,
      href: "/catalog",
      roles: ["super_admin", "clinic_admin", "audiologist", "sales_crm", "head", "business_team"]
    },
    {
      type: "divider",
      roles: ["super_admin", "clinic_admin", "head"]
    },
    {
      label: "Clinics",
      icon: Building2,
      href: "/clinics",
      roles: ["super_admin", "head"]
    },
    {
      label: "Clusters",
      icon: Layers,
      href: "/clusters",
      roles: ["super_admin", "head"]
    },
    {
      label: "Users",
      icon: UserCog,
      href: "/users",
      roles: ["super_admin", "clinic_admin", "head"]
    },
    {
      label: "Reports",
      icon: BarChart3,
      href: "/reports",
      roles: ["super_admin", "clinic_admin", "head"]
    },
    {
      label: "Zoho Integration",
      icon: Cloud,
      href: "/zoho",
      roles: ["super_admin", "clinic_admin"]
    },
    {
      label: "Audit Logs",
      icon: FileText,
      href: "/audit-logs",
      roles: ["super_admin", "head"]
    }
  ];

  const filteredNavItems = navItems.filter(
    item => item.type === "divider" ? item.roles.includes(user?.role) : item.roles.includes(user?.role)
  );

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + "/");

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-30 flex items-center justify-between px-4">
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-sky-600 rounded-lg flex items-center justify-center">
            <Ear className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-slate-900">HearClear</span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <div className="w-8 h-8 bg-sky-600 rounded-full flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0) || "U"}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-screen w-64 bg-white border-r border-slate-200 z-40
        transform transition-transform duration-200 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-slate-100">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sky-600 rounded-xl flex items-center justify-center">
              <Ear className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900 font-['Manrope']">HearClear</h1>
              <p className="text-xs text-slate-500">Audiology Platform</p>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <ScrollArea className="h-[calc(100vh-8rem)]">
          <nav className="p-4 space-y-1">
            {filteredNavItems.map((item, idx) => (
              item.type === "divider" ? (
                <div key={idx} className="h-px bg-slate-100 my-4" />
              ) : (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                    transition-colors duration-150
                    ${isActive(item.href) 
                      ? "bg-sky-50 text-sky-700 border-l-2 border-sky-600" 
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }
                  `}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <item.icon className={`w-5 h-5 ${isActive(item.href) ? "text-sky-600" : "text-slate-400"}`} />
                  <span className="flex-1">{item.label}</span>
                  {item.badge && (
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold text-white rounded ${
                      item.badge === "LIVE" ? "bg-red-500 animate-pulse" : "bg-emerald-500"
                    }`}>
                      {item.badge}
                    </span>
                  )}
                </Link>
              )
            ))}
          </nav>
        </ScrollArea>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-100 bg-white">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start gap-3 px-3">
                <div className="w-8 h-8 bg-sky-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                  {user?.name?.charAt(0) || "U"}
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role?.replace("_", " ")}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        {/* Top Bar (Desktop) */}
        <header className="hidden lg:flex h-16 items-center justify-between px-8 bg-white border-b border-slate-200">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-slate-900 capitalize">
              {location.pathname.split("/")[1]?.replace("-", " ") || "Dashboard"}
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="w-5 h-5 text-slate-500" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2">
                  <div className="w-8 h-8 bg-sky-600 rounded-full flex items-center justify-center text-white font-medium">
                    {user?.name?.charAt(0) || "U"}
                  </div>
                  <span className="text-sm font-medium text-slate-700">{user?.name}</span>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                  <p className="text-xs text-sky-600 capitalize mt-1">{user?.role?.replace("_", " ")}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <div className="fade-in">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
